import { isStandaloneTeamsApp } from './src/isStandaloneTeamsApp';

export enum UpdatesPageName {
	projects = 'projects',
	goals = 'goals',
	questions = 'questions',
	tag = 'tag',
	team = 'team',
	yourWork = 'yourwork',
	savedView = 'view',
}

/**
 * Page names / IDs
 */
export const HOME_PAGE = 'home';
export const STATUS_UPDATES_PAGE = 'statusUpdates';
export const PROJECT_FEED_PAGE = 'projectUpdateList';
export const GOAL_FEED_PAGE = 'goalUpdateList';
export const TAG_FEED_PAGE = 'tagUpdateList';
export const RECENTS_PAGE = 'recents';
export const TEAM_FEED_PAGE = 'teamUpdateList';
export const SAVED_VIEW_FEED_PAGE = 'savedViewUpdateList';
export const READING_MODE_PAGE = 'readingMode';
export const PROJECT_DIRECTORY_PAGE = 'channelList';
export const PROJECT_DIRECTORY_ARCHIVED_PAGE = 'archived-project-directory';
export const PROJECT_DIRECTORY_FOLLOWING_PAGE = 'following-project-directory';
export const INFERRED_PROJECT_PAGE = 'inferred-project';
export const INFERRED_PROJECT_PATH = '/graph/inferred-project/:workspaceId/:projectId';
export const PROJECT_PAGE = 'channel';
export const AGG_PROJECT_PAGE = 'agg-project';
export const PROJECT_APP_SETTINGS_PAGE = 'project-settings';
export const GOAL_PAGE = 'goal';
export const AGG_GOAL_PAGE = 'agg-goal';
export const GOAL_DIRECTORY_PAGE = 'goals';
export const GOAL_DIRECTORY_ARCHIVED_PAGE = 'archived-goals-directory';
export const GOAL_DIRECTORY_FOLLOWING_PAGE = 'following-goals-directory';
export const AGG_GOAL_DIRECTORY_PAGE = 'agg-goals';
export const AGG_GOAL_DIRECTORY_ARCHIVED_PAGE = 'archived-agg-goals-directory';
export const AGG_GOAL_DIRECTORY_FOLLOWING_PAGE = 'following-agg-goals-directory';
export const GOAL_APP_SETTINGS_PAGE = 'goal-settings';
export const TAGS_DIRECTORY = 'tags';
export const FULL_PAGE_NOTIFICATIONS_PAGE = 'fullPageNotifications';
export const FULL_PAGE_SEARCH_PAGE = 'FullPageSearch';
export const TEAM_FULL_PAGE_SEARCH_PAGE = 'TeamFullPageSearch';
export const GOAL_FULL_PAGE_SEARCH_PAGE = 'GoalFullPageSearch';
export const ROVO_SEARCH_PAGE = 'RovoSearch';
export const PROJECTS_FULL_PAGE_SEARCH_PAGE = 'ProjectsFullPageSearch';
export const FULL_PAGE_CHAT_PAGE = 'FullPageChat';
export const FULL_PAGE_AGENTS_PAGE = 'FullPageAgents';
export const CREATE_AGENT_PAGE = 'CreateAgent';
export const EDIT_AGENT_PAGE = 'EditAgent';
export const SHARED_CONVERSATION_PREVIEW_PAGE = 'SharedConversationPreview';
export const STAFF_DIRECTORY_PAGE = 'directory';
export const TEAM_DIRECTORY_PAGE = 'teams-directory';
export const TEAMS_APP_AGENTS_DIRECTORY_PAGE = 'teams-app-agents-directory';
export const TEAMS_APP_FOR_YOU_PAGE = 'teams-app-for-you';
export const KUDOS_DIRECTORY_PAGE = 'kudos-directory';
export const PEOPLE_DIRECTORY_PAGE = 'people-directory';
export const TEAM_SETTINGS_PAGE = 'team-settings';
export const PROFILE_PAGE = 'profile-page';
export const PROFILE_WORK_PAGE = 'profile-work-page';
export const TEAM_PROFILE_PAGE = 'team-profile-page';
export const AGENT_PROFILE_PAGE = 'agent-profile-page';
export const KUDOS_PAGE = 'kudos-profile-page';
export const SETTINGS_PAGE = 'settings';
export const TOPIC_PAGE = 'tag';
export const DASHBOARD_PAGE = 'dashboard';
export const DASHBOARD_BY_ID_PAGE = 'dashboard-by-id';
export const DASHBOARD_DIRECTORY_PAGE = 'dashboard-directory';
export const DASHBOARD_EMBED_PAGE = 'dashboard-embed';
export const DISCOVER_PAGE = 'discover';
export const STARRED_PAGE = 'starred';
export const APPS_PAGE = 'apps';
export const HOMETAB_PAGE = 'hometab';

export const HOME_ROUTES: readonly [
	'hometab',
	'discover',
	'starred',
	'apps',
	'recents',
	'fullPageNotifications',
	'FullPageSearch',
	'RovoSearch',
	'FullPageChat',
	'FullPageAgents',
	'SharedConversationPreview',
	'projectUpdateList',
	'goalUpdateList',
	'tagUpdateList',
	'teamUpdateList',
	'savedViewUpdateList',
	'dashboard',
	'dashboard-directory',
] = [
	HOMETAB_PAGE,
	DISCOVER_PAGE,
	STARRED_PAGE,
	APPS_PAGE,
	RECENTS_PAGE,
	FULL_PAGE_NOTIFICATIONS_PAGE,
	FULL_PAGE_SEARCH_PAGE,
	ROVO_SEARCH_PAGE,
	FULL_PAGE_CHAT_PAGE,
	FULL_PAGE_AGENTS_PAGE,
	SHARED_CONVERSATION_PREVIEW_PAGE,
	PROJECT_FEED_PAGE,
	GOAL_FEED_PAGE,
	TAG_FEED_PAGE,
	TEAM_FEED_PAGE,
	SAVED_VIEW_FEED_PAGE,
	DASHBOARD_PAGE,
	DASHBOARD_DIRECTORY_PAGE,
] as const;

export type HomeRouteName = (typeof HOME_ROUTES)[number];

export const GOAL_APP_ROUTES: readonly [
	'goal',
	'agg-goal',
	'goals',
	'archived-goals-directory',
	'following-goals-directory',
	'GoalFullPageSearch',
	'agg-goals',
	'archived-agg-goals-directory',
	'following-agg-goals-directory',
] = [
	GOAL_PAGE,
	AGG_GOAL_PAGE,
	GOAL_DIRECTORY_PAGE,
	GOAL_DIRECTORY_ARCHIVED_PAGE,
	GOAL_DIRECTORY_FOLLOWING_PAGE,
	GOAL_FULL_PAGE_SEARCH_PAGE,
	AGG_GOAL_DIRECTORY_PAGE,
	AGG_GOAL_DIRECTORY_ARCHIVED_PAGE,
	AGG_GOAL_DIRECTORY_FOLLOWING_PAGE,
] as const;

export type GoalAppRouteName = (typeof GOAL_APP_ROUTES)[number];

export const PROJECT_APP_ROUTES: readonly [
	'channel',
	'agg-project',
	'channelList',
	'archived-project-directory',
	'following-project-directory',
	'ProjectsFullPageSearch',
] = [
	PROJECT_PAGE,
	AGG_PROJECT_PAGE,
	PROJECT_DIRECTORY_PAGE,
	PROJECT_DIRECTORY_ARCHIVED_PAGE,
	PROJECT_DIRECTORY_FOLLOWING_PAGE,
	PROJECTS_FULL_PAGE_SEARCH_PAGE,
] as const;

export type ProjectAppRouteName = (typeof PROJECT_APP_ROUTES)[number];

/**
 * Path prefixes, to be used as part of a longer route-able URL.
 * Mostly used to ensure we keep a whitelist of allowed path prefix
 *
 * WARNING: If you're adding a new path prefix, you'll need to whitelist it below
 * AND add a new global-edge routing rule in the service descriptor.
 */
export const PATH_PREFIX = {
	INFERRED_PROJECT: '/graph/inferred-project',
	PROJECT: '/project',
	AGG_PROJECT: '/agg-project',
	GOAL: '/goal',
	AGG_GOAL: '/agg-goal',
	PEOPLE: '/people',
	LABEL: '/label',
	TOPIC: '/tag',
	AI_TOPIC: '/topic',
	HELP: '/help',
	UPDATES: '/updates',
	FOURZEROTHREE: '/403',
	FORGE_APP: '/app',
	BILLING: '/billing',
	SETTINGS: '/settings',
	HOW: '/how',
	ADMIN: '/admin',
	ACTION: '/action',
	EMBEDS: '/embeds',
	READING_MODE: '/reading',
	KUDOS: '/kudos',
	CREATE_KUDOS_EMBED_OLD: '/give-kudos',
	POPUP_AUTH_COMPLETE: '/atlas-pop-up-auth-complete',
	YOUR_WORK: '/your-work',
	USAGE: '/usage',
	PROJECT_DIRECTORY: '/projects',
	GOAL_DIRECTORY: '/goals',
	AGG_GOAL_DIRECTORY: '/agg-goals',
	ONBOARDING: '/hello',
	NO_ACCESS: '/noaccess',
	TOPICS: '/topics',
	TAGS: '/tags',
	RECENTS: '/recents',
	JIRA_INTEGRATION_READY: '/jira-integration-ready',
	CONFLUENCE: '/wiki',
	JIRA: '/jira',
	JIRA_ISSUES: '/issues',
	JIRA_SECURE: '/secure',
	HEALTH: '/health',
	NOTIFICATIONS: '/notifications',
	FULL_PAGE_SEARCH: '/search',
	ROVO_SEARCH: '/rovosearch',
	FULL_PAGE_CHAT: '/chat',
	AGENTS: '/agents',
	APPS: '/apps',
	/**
	 * 'as const' is IMPORTANT. DO NOT REMOVE
	 * It tells Typescript this 'PATH_PREFIX' object is read only, thus allowing TS to coalesce the object type from
	 *   { [ key: string ]: string }
	 * into a more specific and useful
	 *   { [ key: string ]: '/project' | '/goal' | 'etc' }
	 */
} as const;
// Get the value type of PATH_PREFIX, as in: '/project' | '/goal' | 'etc'
export type AllowedPathPrefix = (typeof PATH_PREFIX)[keyof typeof PATH_PREFIX];
// TS type to enforce route path must contain one of the whitelisted prefix above
type AllowedPath = `${AllowedPathPrefix}${string}`;
export type AllowedPathType = '/' | '/*' | AllowedPath;

/**
 * Did you add a new path prefix? Did you read the warning above?
 */
/**
 * Full paths to actual pages
 */
const { READING_MODE, UPDATES, TOPIC, PEOPLE, ONBOARDING, NOTIFICATIONS } = PATH_PREFIX;

export const HOME_PATH = '/';
export const YOUR_WORK_PATH: '/your-work/:tabName' = `${PATH_PREFIX.YOUR_WORK}/:tabName`;
export const APPS_PATH: '/apps' = PATH_PREFIX.APPS;
export const USAGE_PATH: '/usage' = PATH_PREFIX.USAGE;
export const READING_MODE_PATH: AllowedPath = `${READING_MODE}/:updateType/:inputSource?`;
export const PROJECT_FEED_PATH: AllowedPath = `${UPDATES}/${UpdatesPageName.projects}`;
export const GOAL_FEED_PATH: AllowedPath = `${UPDATES}/${UpdatesPageName.goals}`;
export const GOAL_APP_FEED_PATH: AllowedPath = `${PATH_PREFIX.GOAL_DIRECTORY}/updates`;
export const GOALS_APP_READING_MODE_PATH: AllowedPath = `${PATH_PREFIX.GOAL_DIRECTORY}${READING_MODE}/:updateType/:inputSource?`;
export const PROJECTS_APP_FEED_PATH: AllowedPath = `${PATH_PREFIX.PROJECT_DIRECTORY}/updates`;
export const PROJECTS_APP_READING_MODE_PATH: AllowedPath = `${PATH_PREFIX.PROJECT_DIRECTORY}${READING_MODE}/:updateType/:inputSource?`;
export const TAG_FEED_PATH_PREFIX: AllowedPath = `${UPDATES}/${UpdatesPageName.tag}`;
export const TOPIC_PAGE_PATH_PREFIX: '/tag' = TOPIC;
export const TOPIC_PAGE_PATH_PATTERN: AllowedPath = `${TOPIC}/:uuid/:tabName?/:viewMode?`;
export const RECENTS_PATH: AllowedPath = '/recents';
export const TEAM_FEED_PATH_PREFIX: AllowedPath = `${UPDATES}/${UpdatesPageName.team}`;
export const SAVED_VIEW_FEED_PATH_PREFIX: AllowedPath = `${UPDATES}/${UpdatesPageName.savedView}`;
export const GOALS_APP_SAVED_VIEW_FEED_PATH_PREFIX: AllowedPath = `${PATH_PREFIX.GOAL_DIRECTORY}${UPDATES}/${UpdatesPageName.savedView}`;
export const PROJECTS_APP_SAVED_VIEW_FEED_PATH_PREFIX: AllowedPath = `${PATH_PREFIX.PROJECT_DIRECTORY}${UPDATES}/${UpdatesPageName.savedView}`;
export const PROJECT_DIRECTORY_PATH: '/projects' = PATH_PREFIX.PROJECT_DIRECTORY;
export const PROJECT_DIRECTORY_ARCHIVED_PATH: AllowedPath = `${PATH_PREFIX.PROJECT_DIRECTORY}/archived`;
export const PROJECT_DIRECTORY_FOLLOWING_PATH: AllowedPath = `${PATH_PREFIX.PROJECT_DIRECTORY}/following`;
export const PROJECTS_FULL_PAGE_SEARCH_PATH: AllowedPath = `${PATH_PREFIX.PROJECT_DIRECTORY}/search`;
export const GOAL_DIRECTORY_PATH: '/goals' = PATH_PREFIX.GOAL_DIRECTORY;
export const GOAL_DIRECTORY_ARCHIVED_PATH: AllowedPath = `${PATH_PREFIX.GOAL_DIRECTORY}/archived`;
export const GOAL_DIRECTORY_FOLLOWING_PATH: AllowedPath = `${PATH_PREFIX.GOAL_DIRECTORY}/following`;
export const GOAL_FULL_PAGE_SEARCH_PATH: AllowedPath = `${PATH_PREFIX.GOAL_DIRECTORY}/search`;
export const AGG_GOAL_DIRECTORY_PATH: '/agg-goals' = PATH_PREFIX.AGG_GOAL_DIRECTORY;
export const AGG_GOAL_DIRECTORY_ARCHIVED_PATH: AllowedPath = `${PATH_PREFIX.AGG_GOAL_DIRECTORY}/archived`;
export const AGG_GOAL_DIRECTORY_FOLLOWING_PATH: AllowedPath = `${PATH_PREFIX.AGG_GOAL_DIRECTORY}/following`;

export const ONBOARDING_PATH: '/hello' = ONBOARDING;
export const NO_ACCESS_PATH: '/noaccess' = PATH_PREFIX.NO_ACCESS;
export const FULL_PAGE_NOTIFICATIONS_PATH: AllowedPath = NOTIFICATIONS;
export const FULL_PAGE_SEARCH_PATH: AllowedPath = '/search';
export const ROVO_SEARCH_PATH: AllowedPath = '/rovosearch';
export const FULL_PAGE_CHAT: AllowedPath = '/chat';
export const FULL_PAGE_AGENTS: AllowedPath = PATH_PREFIX.AGENTS;
export const CREATE_AGENT: AllowedPath = `${PATH_PREFIX.FULL_PAGE_CHAT}/agents/new`;
export const EDIT_AGENT: AllowedPath = `${PATH_PREFIX.FULL_PAGE_CHAT}/agents/:agentId/edit`;
export const SHARED_CONVERSATION_PREVIEW_SHARE_TOKEN = 'preview';
export const SHARED_CONVERSATION_PREVIEW: AllowedPath = `${PATH_PREFIX.FULL_PAGE_CHAT}/share/:shareToken`;

export const TAGS_PATH: '/tags' = PATH_PREFIX.TAGS;

export const PROJECT_PATH_PREFIX: '/project' = PATH_PREFIX.PROJECT;
export const AGG_PROJECT_PATH_PREFIX: '/agg-project' = PATH_PREFIX.AGG_PROJECT;
export const GOAL_PATH_PREFIX: '/goal' = PATH_PREFIX.GOAL;
export const AGG_GOAL_PATH_PREFIX: '/agg-goal' = PATH_PREFIX.AGG_GOAL;
export const LABEL_PATH_PREFIX: '/label' = PATH_PREFIX.LABEL;

export const PROJECT_PATH_PATTERN: AllowedPath = `${PROJECT_PATH_PREFIX}/:id/:tabName?/:resourceId?`;
export const AGG_PROJECT_PATH_PATTERN: AllowedPath = `${AGG_PROJECT_PATH_PREFIX}/:id/:tabName?/:resourceId?`;
export const GOAL_PATH_PATTERN: AllowedPath = `${GOAL_PATH_PREFIX}/:id/:tabName?/:resourceId?`;
export const AGG_GOAL_PATH_PATTERN: AllowedPath = `${AGG_GOAL_PATH_PREFIX}/:id/:tabName?/:resourceId?`;

// Teams app
export const PEOPLE_DIRECTORY_PATH: '/people' | '/' = isStandaloneTeamsApp() ? HOME_PATH : PEOPLE;
type TeamsAppAllowedPath = `${typeof PEOPLE_DIRECTORY_PATH}${string}`;
export const PEOPLE_KUDOS_PATH_PATTERN: TeamsAppAllowedPath = `${PEOPLE_DIRECTORY_PATH}/kudos/:id`;
export const PEOPLE_TEAM_PATH_PATTERN: TeamsAppAllowedPath = `${PEOPLE_DIRECTORY_PATH}/team/:id/:tabId?`;
export const PEOPLE_TEAM_EMBED_PATH_PATTERN: TeamsAppAllowedPath = `${PEOPLE_DIRECTORY_PATH}/team/:id/embed`;
export const PEOPLE_AGENT_PATH_PATTERN: TeamsAppAllowedPath = `${PEOPLE_DIRECTORY_PATH}/agent/:id`;
export const TEAM_PATH: TeamsAppAllowedPath = `${PEOPLE_DIRECTORY_PATH}/team`;
export const AGENT_PATH: TeamsAppAllowedPath = `${PEOPLE_DIRECTORY_PATH}/agent`;
export const TEAM_SETTINGS_PATH: TeamsAppAllowedPath = `${PEOPLE_DIRECTORY_PATH}/settings`;
export const TEAM_PATH_PATTERN: TeamsAppAllowedPath = `${PEOPLE_DIRECTORY_PATH}/:id`;
export const AGENT_EMBED_PATH: TeamsAppAllowedPath = `${PEOPLE_DIRECTORY_PATH}/agent/:agentId/embed`;
export const TEAM_FULL_PAGE_SEARCH_PATH: TeamsAppAllowedPath = `${PEOPLE_DIRECTORY_PATH}/global-search`;

export const GOAL_APP_SETTINGS_PATH: AllowedPath = `${PATH_PREFIX.GOAL_DIRECTORY}${PATH_PREFIX.SETTINGS}`;
export const PROJECT_APP_SETTINGS_PATH: AllowedPath = `${PATH_PREFIX.PROJECT_DIRECTORY}${PATH_PREFIX.SETTINGS}`;

export const NAME_EMBED_SUFFIX = 'embed';

export const DASHBOARDS_PATH = '/dashboards';
export const DASHBOARD_BY_ID_PATH = '/d';

export const NO_WORKSPACE_UPDATES_PATH: AllowedPath = UPDATES;
export const NO_WORKSPACE_PATHS: string[] = [
	ONBOARDING_PATH,
	GOAL_DIRECTORY_PATH,
	PROJECT_DIRECTORY_PATH,
	PEOPLE_DIRECTORY_PATH,
	YOUR_WORK_PATH,
	FULL_PAGE_NOTIFICATIONS_PATH,
	FULL_PAGE_CHAT,
	FULL_PAGE_AGENTS,
	CREATE_AGENT,
	EDIT_AGENT,
	SHARED_CONVERSATION_PREVIEW,
	NO_WORKSPACE_UPDATES_PATH,
];

export const EMBED_ROUTE_NAMES: {
	label: 'label-embed';
	goalsDirectory: 'goal-embed';
	projectsDirectory: 'project-embed';
	projectEmbed: 'project-embed-embed';
	projectAbout: 'project-about-embed';
	goalAbout: 'goal-about-embed';
	goalEmbed: 'goal-embed-embed';
	createKudosOld: 'create-kudos-old-embed';
	createKudos: 'create-kudos-embed';
	agentEmbed: 'agent-embed';
	teamEmbed: 'team-embed';
} = {
	label: `label-${NAME_EMBED_SUFFIX}` as const,
	goalsDirectory: `goal-${NAME_EMBED_SUFFIX}` as const,
	projectsDirectory: `project-${NAME_EMBED_SUFFIX}` as const,
	projectEmbed: `project-embed-${NAME_EMBED_SUFFIX}` as const,
	projectAbout: `project-about-${NAME_EMBED_SUFFIX}` as const,
	goalAbout: `goal-about-${NAME_EMBED_SUFFIX}` as const,
	goalEmbed: `goal-embed-${NAME_EMBED_SUFFIX}` as const,
	createKudosOld: `create-kudos-old-${NAME_EMBED_SUFFIX}` as const,
	createKudos: `create-kudos-${NAME_EMBED_SUFFIX}` as const,
	agentEmbed: `agent-${NAME_EMBED_SUFFIX}` as const,
	teamEmbed: `team-${NAME_EMBED_SUFFIX}` as const,
};
